/**
 * SlateReplay — Phase 4.B
 *
 * Pick a date, see what bets were placed that day with their final
 * outcomes. Per-sport breakdown, P&L, calibration retrospective.
 *
 * Pure frontend filter over /api/bets — no new endpoints. Future
 * (Phase 5 replay scrubber) can extend this with full pipeline-state
 * snapshots from R2.
 */
import { html, type TemplateResult } from "lit-html";
import { store } from "../data/store";
import type { BetsResponse, Bet } from "../data/types";
import { renderExplainButton } from "./ExplainButton";

interface ViewState {
  selectedDate: string | null;        // YYYY-MM-DD
}
const view: ViewState = {
  selectedDate: null,
};

const SPORT_COLORS: Record<string, string> = {
  NBA:    "var(--accent-orange-light)",
  WNBA:   "#2dd4bf",
  MLB:    "var(--accent-blue-light)",
  NHL:    "var(--accent-purple-light)",
  SOCCER: "var(--accent-green-light)",
  UFC:    "var(--accent-red-light)",
};

export function renderSlateReplay(onChange: () => void): TemplateResult {
  const e = store.get<BetsResponse>("bets");
  if (e.error) return errorView(e.error);
  if (!e.data && e.loading) return skeletonView();
  if (!e.data) return emptyView();

  const all = e.data.bets;
  const datesAvailable = uniqueDates(all);
  if (!view.selectedDate && datesAvailable.length > 0) {
    // Default to the most recent date with at least 5 bets
    const recent = datesAvailable.find((d) => all.filter((b) => b.date === d).length >= 5)
                ?? datesAvailable[0]!;
    view.selectedDate = recent;
  }

  const dayBets = view.selectedDate
    ? all.filter((b) => b.date === view.selectedDate)
    : [];

  return html`
    <div class="replay">
      ${headerCard(datesAvailable, dayBets, onChange)}

      ${dayBets.length === 0 ? emptyDayView() : html`
        <div class="replay__layout">
          ${perSportSummary(dayBets)}
          ${betList(dayBets)}
        </div>
      `}
    </div>
  `;
}

// =============================================================
// Header — date picker + day totals
// =============================================================
function headerCard(
  dates: string[],
  dayBets: Bet[],
  onChange: () => void,
): TemplateResult {
  const decided = dayBets.filter((b) => b.outcome === "win" || b.outcome === "loss");
  const wins    = decided.filter((b) => b.outcome === "win").length;
  const pnlSum  = dayBets.reduce((s, b) => s + (b.pnl ?? 0), 0);
  const winRate = decided.length > 0 ? (wins / decided.length) * 100 : null;

  // Quick-jump dates: today, yesterday, 7d ago
  const today  = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(Date.now() - 86400_000).toISOString().slice(0, 10);
  const sevenAgo  = new Date(Date.now() - 7 * 86400_000).toISOString().slice(0, 10);
  const quick = [
    { label: "today",     date: today },
    { label: "yesterday", date: yesterday },
    { label: "-7d",       date: sevenAgo },
  ];

  return html`
    <section class="panel replay-header">
      <div class="replay-header__row">
        <div class="replay-header__metric">
          <span class="replay-header__label">date</span>
          <input
            class="replay-header__date"
            type="date"
            min=${dates.at(-1) ?? ""}
            max=${dates[0] ?? today}
            .value=${view.selectedDate ?? today}
            @change=${(e: Event) => {
              view.selectedDate = (e.target as HTMLInputElement).value;
              onChange();
            }}
          />
          <div class="replay-header__quick">
            ${quick.map((q) => html`
              <button
                class="replay-chip ${view.selectedDate === q.date ? "is-active" : ""}"
                @click=${() => { view.selectedDate = q.date; onChange(); }}
              >
                ${q.label}
              </button>
            `)}
          </div>
        </div>

        <div class="replay-header__metric">
          <span class="replay-header__label">posted</span>
          <span class="replay-header__value">${dayBets.length}</span>
          <span class="replay-header__sub">${decided.length} decided</span>
        </div>

        <div class="replay-header__metric">
          <span class="replay-header__label">win rate</span>
          <span class="replay-header__value">
            ${winRate != null ? `${winRate.toFixed(1)}%` : "—"}
          </span>
          ${decided.length > 0 ? html`
            <span class="replay-header__sub">${wins}W ${decided.length - wins}L</span>
          ` : ""}
        </div>

        <div class="replay-header__metric">
          <span class="replay-header__label">P&amp;L</span>
          <span class="replay-header__value ${pnlSum > 0 ? "is-pos" : pnlSum < 0 ? "is-neg" : ""}">
            ${pnlSum >= 0 ? "+" : ""}${pnlSum.toFixed(2)}u
          </span>
        </div>

        <span class="replay-header__spacer"></span>

        ${renderExplainButton({
          label: "Explain this slate",
          context: () => buildSlateExplainContext(view.selectedDate, dayBets),
        })}
      </div>
    </section>
  `;
}

// =============================================================
// Per-sport summary cards
// =============================================================
function perSportSummary(dayBets: Bet[]): TemplateResult {
  const bySport: Record<string, Bet[]> = {};
  for (const b of dayBets) {
    const k = (b.sport || "?").toUpperCase();
    bySport[k] = bySport[k] ?? [];
    bySport[k]!.push(b);
  }
  const sports = Object.keys(bySport).sort();

  return html`
    <aside class="replay-sports">
      ${sports.map((s) => sportCard(s, bySport[s]!))}
    </aside>
  `;
}

function sportCard(sport: string, bets: Bet[]): TemplateResult {
  const decided = bets.filter((b) => b.outcome === "win" || b.outcome === "loss");
  const wins    = decided.filter((b) => b.outcome === "win").length;
  const wr = decided.length > 0 ? (wins / decided.length) * 100 : null;
  const pnl = bets.reduce((s, b) => s + (b.pnl ?? 0), 0);
  const color = SPORT_COLORS[sport] ?? "var(--text-secondary)";

  // LLM confidence histogram: count by bucket
  const buckets = { "70+": 0, "50-69": 0, "<50": 0, "?": 0 };
  for (const b of bets) {
    const c = b.llm_confidence;
    if (c == null) buckets["?"]++;
    else if (c >= 70) buckets["70+"]++;
    else if (c >= 50) buckets["50-69"]++;
    else              buckets["<50"]++;
  }

  return html`
    <section class="panel replay-sport-card">
      <div class="replay-sport-card__head">
        <span class="replay-sport-card__name" style="color:${color};">${sport}</span>
        <span class="replay-sport-card__n">${bets.length} bets</span>
      </div>
      <div class="replay-sport-card__metrics">
        <span>WR <strong>${wr != null ? wr.toFixed(0) + "%" : "—"}</strong></span>
        <span>${decided.length}/${bets.length} decided</span>
        <span class="${pnl > 0 ? "is-pos" : pnl < 0 ? "is-neg" : ""}">
          <strong>${pnl >= 0 ? "+" : ""}${pnl.toFixed(2)}u</strong>
        </span>
      </div>
      <div class="replay-sport-card__buckets">
        ${(["70+", "50-69", "<50", "?"] as const).map((bk) => html`
          <span class="replay-sport-card__bucket">
            <span class="replay-sport-card__bucket-label">${bk}</span>
            <span class="replay-sport-card__bucket-n">${buckets[bk]}</span>
          </span>
        `)}
      </div>
    </section>
  `;
}

// =============================================================
// Bet list — sorted by outcome (won first, then lost, then pending)
// =============================================================
function betList(dayBets: Bet[]): TemplateResult {
  const ordered = [...dayBets].sort((a, b) => {
    const orderRank = (b: Bet) => {
      if (b.outcome === "win")  return 0;
      if (b.outcome === "loss") return 1;
      if (b.outcome === "void") return 2;
      return 3;
    };
    const r = orderRank(a) - orderRank(b);
    if (r !== 0) return r;
    return (b.pnl ?? 0) - (a.pnl ?? 0);
  });

  return html`
    <main class="panel replay-bets">
      <header class="replay-bets__head">
        <h3>Bets · ${dayBets.length}</h3>
        <span class="replay-bets__sub">sorted by outcome → P&amp;L</span>
      </header>
      <div class="replay-bets__list">
        ${ordered.map(betRow)}
      </div>
    </main>
  `;
}

function betRow(b: Bet): TemplateResult {
  const color = SPORT_COLORS[(b.sport || "?").toUpperCase()] ?? "var(--text-secondary)";
  const outcome = b.outcome || "pending";
  const matchup = b.player ?? b.matchup ?? `${b.team ?? ""} vs ${b.opp ?? ""}`;
  return html`
    <div class="replay-bet replay-bet--${outcome}">
      <span class="replay-bet__sport"
             style="color:${color};">${(b.sport || "?").toUpperCase()}</span>
      <span class="replay-bet__matchup">${matchup}</span>
      <span class="replay-bet__market">${b.market ?? "—"} ${b.side ?? ""} ${b.line ?? ""}</span>
      <span class="replay-bet__llm">
        ${b.llm_confidence != null ? `${b.llm_confidence}` : "—"}
      </span>
      <span class="replay-bet__outcome replay-bet__outcome--${outcome}">${outcome}</span>
      <span class="replay-bet__pnl ${b.pnl != null && b.pnl > 0 ? "is-pos" : b.pnl != null && b.pnl < 0 ? "is-neg" : ""}">
        ${b.pnl == null ? "—" : `${b.pnl > 0 ? "+" : ""}${b.pnl.toFixed(2)}u`}
      </span>
    </div>
  `;
}

// =============================================================
// Helpers
// =============================================================
function uniqueDates(bets: Bet[]): string[] {
  const set = new Set<string>();
  for (const b of bets) {
    if (b.date) set.add(b.date);
  }
  return Array.from(set).sort().reverse();   // newest first
}

function buildSlateExplainContext(date: string | null, bets: Bet[]): string {
  if (!date) return "(no date selected)";
  const decided = bets.filter((b) => b.outcome === "win" || b.outcome === "loss");
  const wins = decided.filter((b) => b.outcome === "win").length;
  const pnl = bets.reduce((s, b) => s + (b.pnl ?? 0), 0);

  const bySport: Record<string, { n: number; w: number; l: number; pnl: number }> = {};
  for (const b of bets) {
    const k = (b.sport || "?").toUpperCase();
    bySport[k] = bySport[k] ?? { n: 0, w: 0, l: 0, pnl: 0 };
    bySport[k].n++;
    if (b.outcome === "win")  bySport[k].w++;
    if (b.outcome === "loss") bySport[k].l++;
    bySport[k].pnl += b.pnl ?? 0;
  }

  const lines = [
    `Slate replay: ${date}`,
    `${bets.length} bets posted · ${decided.length} decided · ${wins}W ${decided.length - wins}L · P&L ${pnl.toFixed(2)}u`,
    "",
    "Per sport:",
    ...Object.entries(bySport).map(([s, v]) =>
      `  ${s}: ${v.n}n · ${v.w}W ${v.l}L · ${v.pnl.toFixed(2)}u`,
    ),
    "",
    "Notable bets:",
    ...bets.filter((b) => b.outcome === "loss" && (b.pnl ?? 0) <= -0.5).slice(0, 5).map((b) =>
      `  LOSS ${b.sport} ${b.player ?? b.matchup} ${b.market} ${b.side} ${b.line}: ${b.pnl}u, conf=${b.llm_confidence ?? "?"}`,
    ),
    ...bets.filter((b) => b.outcome === "win" && (b.pnl ?? 0) >= 0.5).slice(0, 3).map((b) =>
      `  WIN  ${b.sport} ${b.player ?? b.matchup} ${b.market} ${b.side} ${b.line}: +${b.pnl}u, conf=${b.llm_confidence ?? "?"}`,
    ),
  ];
  return lines.join("\n");
}

// =============================================================
// State views
// =============================================================
function skeletonView(): TemplateResult {
  return html`
    <div class="replay">
      <section class="panel" style="height:80px;"></section>
      <section class="panel" style="min-height:480px;padding:24px;">
        ${Array.from({ length: 6 }, () => html`
          <div class="skeleton" style="height:18px;margin-bottom:8px;"></div>
        `)}
      </section>
    </div>
  `;
}

function emptyView(): TemplateResult {
  return html`
    <div class="panel" style="padding:48px;text-align:center;">
      <div style="color:var(--text-tertiary);font-size:14px;line-height:1.5;
                   max-width:480px;margin:0 auto;">
        No bets yet. Slate replay shows posted bets from any past date.
      </div>
    </div>
  `;
}

function emptyDayView(): TemplateResult {
  return html`
    <section class="panel" style="padding:48px;text-align:center;">
      <div style="color:var(--text-tertiary);font-size:14px;line-height:1.5;">
        No bets on ${view.selectedDate}. Pick a different date.
      </div>
    </section>
  `;
}

function errorView(err: Error): TemplateResult {
  return html`
    <div class="panel" style="padding:32px;border-color:rgba(255,69,58,0.32);">
      <div class="panel__title">
        <span>Slate Replay</span>
        <span class="phase-stub-badge"
              style="background:rgba(255,69,58,0.16);
                      color:var(--accent-red-light);
                      border-color:rgba(255,69,58,0.32);">
          fetch error
        </span>
      </div>
      <div style="color:var(--accent-red-light);font-size:12px;
                   line-height:1.5;font-family:monospace;
                   word-break:break-all;white-space:pre-wrap;
                   margin-top:8px;">
        ${err.message}
      </div>
    </div>
  `;
}
